import React, { useCallback, useEffect, useState } from 'react'
import styled from 'styled-components/macro'
import { FOOD_SAFETY_SECONDARY_TEXT } from 'src/constants/foodSafety'
import useFoodSafetyLatestRequest from 'src/hooks/useFoodSafetyLatestRequest'
import { fetchFoodSafetyDefinitionRevisions } from 'src/utils/foodSafetyApi'
import { listFoodSafetyRangeHistory } from 'src/utils/foodSafetyRangeHistory'

/**
 * Quiet "History" link in the Setup drawer: the ranges this check has had,
 * who changed them and from which day. Loaded only when opened.
 */
const FoodSafetyRangeHistory = ({ venueId, definitionId, section }) => {
  const [open, setOpen] = useState(false)
  const [state, setState] = useState({
    loading: false,
    error: false,
    entries: null,
  })
  const latest = useFoodSafetyLatestRequest()

  const load = useCallback(async () => {
    if (!venueId || !definitionId) return
    const isCurrent = latest.begin()
    setState(prev => ({ ...prev, loading: true, error: false }))
    try {
      const revisions = await fetchFoodSafetyDefinitionRevisions(venueId, {
        definitionId,
      })
      if (!isCurrent()) return
      setState({
        loading: false,
        error: false,
        entries: listFoodSafetyRangeHistory(revisions, section),
      })
    } catch {
      if (!isCurrent()) return
      setState({ loading: false, error: true, entries: null })
    } finally {
      isCurrent.done()
    }
  }, [venueId, definitionId, section, latest])

  useEffect(() => {
    setOpen(false)
    setState({ loading: false, error: false, entries: null })
  }, [definitionId])

  const toggle = () => {
    const next = !open
    setOpen(next)
    if (next) load()
  }

  const panelId = `fs-range-history-${definitionId}`

  return (
    <Wrap>
      <Toggle
        type='button'
        aria-expanded={open}
        aria-controls={panelId}
        onClick={toggle}
        data-testid='fs-range-history-toggle'
      >
        {open ? 'Hide range history' : 'History'}
      </Toggle>
      {open ? (
        <Panel id={panelId} data-testid='fs-range-history'>
          {state.loading && !state.entries ? (
            <Quiet>Loading…</Quiet>
          ) : state.error ? (
            <Quiet data-testid='fs-range-history-error'>
              Couldn’t load the history.{' '}
              <Toggle type='button' onClick={load}>
                Try again
              </Toggle>
            </Quiet>
          ) : state.entries && state.entries.length > 0 ? (
            <List>
              {state.entries.map(entry => (
                <Entry key={entry.key} data-testid='fs-range-history-entry'>
                  <EntryText>{entry.text}</EntryText>
                  {entry.meta ? <Quiet>{entry.meta}</Quiet> : null}
                </Entry>
              ))}
            </List>
          ) : (
            <Quiet data-testid='fs-range-history-empty'>
              No range changes recorded yet.
            </Quiet>
          )}
          <Quiet>Past readings keep the range that applied on the day.</Quiet>
        </Panel>
      ) : null}
    </Wrap>
  )
}

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  margin-bottom: 0.75rem;
`

const Toggle = styled.button`
  align-self: flex-start;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 0.85rem;
  font-weight: 600;
  color: ${FOOD_SAFETY_SECONDARY_TEXT};
  text-decoration: underline;
`

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  padding: 0.6rem 0.75rem;
  border: 1px solid ${({ theme }) => theme.colours?.panSilver || '#ddd'};
  border-radius: 6px;
`

const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
`

const Entry = styled.li`
  display: flex;
  flex-direction: column;
`

const EntryText = styled.span`
  font-weight: 600;
`

const Quiet = styled.span`
  font-size: 0.8rem;
  color: ${FOOD_SAFETY_SECONDARY_TEXT};
`

export default FoodSafetyRangeHistory
